import { useQuery } from '@tanstack/react-query';
import { client } from '@xinfer/api-client';
import type { Account } from '@xinfer/api-client';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CodeBlock,
  CodeTabs,
  CopyButton,
  PageHeader,
  SectionHeader,
  usePageTitle,
  type CodeTab,
} from '@xinfer/ui';
import { Info, TriangleAlert } from 'lucide-react';

/**
 * Everything needed to make the first request, prefilled.
 *
 * The endpoint is OpenAI-compatible on purpose, so the shortest honest
 * quickstart is "point your existing client at this base URL". Say that
 * plainly rather than inventing a bespoke SDK to document.
 *
 * Both the base URL and the model name come from `GET /v1/config` — the
 * deployment, not the build. Neither is derivable here: the gate is a
 * different public name from the portal (api and gate both serve `/v1/`, so
 * they cannot share an origin), and the model is whatever a worker has
 * registered. This page previously reconstructed the base URL from
 * `window.location.origin`, which named the portal, where
 * `/v1/chat/completions` does not exist — so every request copied out of here
 * came back as a bare 404 with an empty body.
 */
export function Quickstart({ account }: { account: Account }) {
  usePageTitle('Quickstart');

  const config = useQuery({
    queryKey: ['service-config'],
    queryFn: () => client.serviceConfig(),
  });

  const baseUrl = config.data?.inference_base_url ?? '';
  // The gate serves one model per worker today, so the first is the one to
  // document. A placeholder rather than a guess when the list is empty: naming
  // a model that is not registered produces a 404 that reads like a broken
  // endpoint rather than a wrong string.
  const model = config.data?.models[0] ?? 'MODEL-ID';
  const modelUnknown = (config.data?.models.length ?? 0) === 0;

  const tabs: CodeTab[] = [
    {
      id: 'curl',
      label: 'curl',
      language: 'shell',
      code: `curl ${baseUrl}/chat/completions \\
  -H "Authorization: Bearer $XINFER_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${model}",
    "messages": [{"role": "user", "content": "hello"}],
    "stream": true
  }'`,
    },
    {
      id: 'python',
      label: 'Python',
      language: 'python',
      code: `import os
from openai import OpenAI

client = OpenAI(
    api_key=os.environ["XINFER_API_KEY"],
    base_url="${baseUrl}",
)

stream = client.chat.completions.create(
    model="${model}",
    messages=[{"role": "user", "content": "hello"}],
    stream=True,
)
for chunk in stream:
    print(chunk.choices[0].delta.content or "", end="")`,
    },
    {
      id: 'node',
      label: 'Node',
      language: 'javascript',
      code: `import OpenAI from 'openai';

const client = new OpenAI({
  apiKey: process.env.XINFER_API_KEY,
  baseURL: '${baseUrl}',
});

const stream = await client.chat.completions.create({
  model: '${model}',
  messages: [{ role: 'user', content: 'hello' }],
  stream: true,
});

for await (const chunk of stream) {
  process.stdout.write(chunk.choices[0]?.delta?.content ?? '');
}`,
    },
  ];

  return (
    <>
      <PageHeader
        title="Quickstart"
        description="The API is OpenAI-compatible, so most existing clients work unmodified."
      />

      <Card>
        <CardHeader>
          <CardTitle>Base URL</CardTitle>
          <CardDescription>
            Point an existing OpenAI client at this and it works — same request shape, same
            streaming chunk format. Note that it is a different host from this portal: the two
            services both serve <code className="font-mono">/v1/</code>, so they cannot share one
            name.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-md border border-border bg-muted/50 px-3 py-2 font-mono text-sm">
            {config.isPending ? 'loading…' : baseUrl}
          </code>
          <CopyButton value={baseUrl} />
        </CardContent>
      </Card>

      <div className="mt-8">
        <SectionHeader
          title="1. Create a key"
          description="On the API keys page. It is shown once, so store it before closing the dialog."
        />
        <CodeBlock
          language="shell"
          code={`export XINFER_API_KEY="sk-..."   # the key from the reveal dialog`}
        />
      </div>

      <div className="mt-8">
        <SectionHeader
          title="2. Make a request"
          description="Streaming, in three clients. The key goes in an Authorization: Bearer header."
        />
        {modelUnknown && !config.isPending && (
          <Alert variant="warning" className="mb-3">
            <TriangleAlert />
            <div>
              <AlertTitle>No model is registered right now</AlertTitle>
              <AlertDescription>
                The examples below say <code className="font-mono">MODEL-ID</code>. Ask the
                endpoint for the real list: <code className="font-mono">curl {baseUrl}/models</code>
                .
              </AlertDescription>
            </div>
          </Alert>
        )}
        <CodeTabs tabs={tabs} />
      </div>

      <div className="mt-8">
        <SectionHeader title="Notes" />
        <div className="space-y-3">
          <Alert>
            <Info />
            <div>
              <AlertTitle>Only two endpoints exist</AlertTitle>
              <AlertDescription>
                <code className="font-mono">/v1/chat/completions</code> and{' '}
                <code className="font-mono">/v1/models</code>. Everything else answers{' '}
                <code className="font-mono">404</code> rather than pretending to work — including
                the same paths on this portal's host, which is a different service.
              </AlertDescription>
            </div>
          </Alert>

          <Alert>
            <Info />
            <div>
              <AlertTitle>Usage is metered per token, by kind</AlertTitle>
              <AlertDescription>
                Cached input, uncached input and output are counted and priced separately — a
                prefix cache hit skips prefill and is billed at its own lower rate. KV-token-seconds
                are recorded alongside them, because held context is what the node is actually
                short of; see Limits for what that caps, and Usage for the breakdown so far.
              </AlertDescription>
            </div>
          </Alert>
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          Account <span className="font-mono">{account.id}</span>
        </p>
      </div>
    </>
  );
}
